import React, { useState, useEffect, useCallback } from 'react';
import { rbacApi } from '../services/api';
import { Button, Card } from '../components/UI';
import { ShieldCheck, ArrowCounterClockwise, FloppyDisk } from '@phosphor-icons/react';

const MODULE_LABELS = {
  dashboard: 'Dashboard', users: 'Users', projects: 'Projects', teams: 'Teams',
  wbs: 'WBS', milestones_tasks: 'Milestones & Tasks',
  daily_logs: 'Daily Logs', manpower: 'Manpower', materials: 'Materials',
  team_reports: 'Team Reports', productivity: 'Productivity',
  budgets: 'Budgets', costs: 'Costs', budget_vs_actual: 'Budget vs Actual',
  invoices: 'Invoices', payments: 'Payments', variations: 'Variations',
  payroll: 'Payroll', accounting: 'Accounting', financial_dashboard: 'Financial Dashboard',
  vat_report: 'VAT Report',
  inventory: 'Inventory',
  documents: 'Documents', approvals: 'Approvals',
  weekly_reports: 'Weekly Reports', advanced_reports: 'Advanced Reports',
  executive_dashboard: 'Executive Dashboard', project_dashboard: 'Project Dashboard',
  site_dashboard: 'Site Dashboard',
  notifications: 'Notifications', calendar: 'Calendar', meetings: 'Meeting Notes',
  settings: 'Settings', tax_settings: 'Tax Settings', legal_admin: 'Legal Admin',
  user_guide: 'User Guide', audit_log: 'Audit Log', role_permissions: 'Role Permissions',
};

const MODULE_GROUPS = [
  { label: 'Planning', modules: ['dashboard', 'users', 'projects', 'teams', 'wbs', 'milestones_tasks'] },
  { label: 'Execution', modules: ['daily_logs', 'manpower', 'materials', 'team_reports', 'productivity'] },
  { label: 'Finance', modules: ['budgets', 'costs', 'budget_vs_actual', 'invoices', 'payments', 'variations', 'payroll', 'accounting', 'financial_dashboard', 'vat_report'] },
  { label: 'Inventory', modules: ['inventory'] },
  { label: 'Documents', modules: ['documents', 'approvals'] },
  { label: 'Reports', modules: ['weekly_reports', 'advanced_reports', 'executive_dashboard', 'project_dashboard', 'site_dashboard'] },
  { label: 'Admin', modules: ['notifications', 'calendar', 'meetings', 'settings', 'tax_settings', 'legal_admin', 'user_guide', 'audit_log', 'role_permissions'] },
];

export default function RolePermissionsPage() {
  const [permissions, setPermissions] = useState({});
  const [allModules, setAllModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await rbacApi.getPermissions();
      setPermissions(res.data.permissions);
      setAllModules(res.data.modules);
      setRoles(res.data.roles);
      setDirty({});
    } catch (e) { setError('Failed to load permissions'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (role, mod) => {
    const current = permissions[role] || [];
    const updated = current.includes(mod) ? current.filter(m => m !== mod) : [...current, mod];
    setPermissions({ ...permissions, [role]: updated });
    setDirty({ ...dirty, [role]: true });
  };

  const toggleGroup = (role, groupModules) => {
    const current = permissions[role] || [];
    const allChecked = groupModules.every(m => current.includes(m));
    let updated;
    if (allChecked) {
      updated = current.filter(m => !groupModules.includes(m));
    } else {
      updated = [...new Set([...current, ...groupModules])];
    }
    setPermissions({ ...permissions, [role]: updated });
    setDirty({ ...dirty, [role]: true });
  };

  const saveRole = async (role) => {
    setSaving(role); setError(''); setSuccess('');
    try {
      await rbacApi.updatePermissions({ role, modules: permissions[role] || [] });
      setDirty({ ...dirty, [role]: false });
      setSuccess(`Permissions saved for ${role}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(''); }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Reset ALL role permissions to system defaults?')) return;
    try {
      await rbacApi.resetDefaults();
      await load();
      setSuccess('Permissions reset to defaults');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="role-permissions-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]" data-testid="rbac-heading">
            <ShieldCheck className="w-6 h-6 inline mr-2 text-[#002FA7]" />Role Permissions
          </h1>
          <p className="text-sm text-[#666]">Configure which modules each role can access. Changes apply immediately.</p>
        </div>
        <Button size="sm" variant="secondary" onClick={resetDefaults} data-testid="reset-defaults-btn">
          <ArrowCounterClockwise className="w-4 h-4 mr-1" /> Reset to Defaults
        </Button>
      </div>

      {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
      {success && <div className="p-2 bg-[#E8F5E9] text-[#00C853] rounded text-sm">{success}</div>}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#E0E0E0]">
                <th className="sticky left-0 z-10 px-3 py-2 text-left font-semibold text-[#0A0A0A] min-w-[160px]" style={{ backgroundColor: 'var(--card-bg)' }}>Module</th>
                {roles.map(role => (
                  <th key={role} className="px-2 py-2 text-center font-semibold text-[#0A0A0A] min-w-[90px] whitespace-nowrap">
                    <div>{role}</div>
                    {dirty[role] && (
                      <button onClick={() => saveRole(role)} disabled={saving === role}
                        className="mt-1 px-2 py-0.5 text-[10px] rounded text-white transition-opacity"
                        style={{ backgroundColor: 'var(--primary)' }} data-testid={`save-${role}`}>
                        {saving === role ? '...' : 'Save'}
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MODULE_GROUPS.map(group => (
                <React.Fragment key={group.label}>
                  <tr className="border-t-2 border-[#E0E0E0]">
                    <td className="sticky left-0 z-10 px-3 py-2 font-bold text-[10px] uppercase tracking-wider text-[#002FA7]" style={{ backgroundColor: 'var(--card-bg)' }}>
                      {group.label}
                    </td>
                    {roles.map(role => (
                      <td key={role} className="px-2 py-2 text-center">
                        <input type="checkbox"
                          checked={group.modules.every(m => (permissions[role] || []).includes(m))}
                          ref={el => {
                            if (el) {
                              const checked = group.modules.filter(m => (permissions[role] || []).includes(m)).length;
                              el.indeterminate = checked > 0 && checked < group.modules.length;
                            }
                          }}
                          onChange={() => toggleGroup(role, group.modules)}
                          className="w-3.5 h-3.5 accent-[#002FA7] cursor-pointer" />
                      </td>
                    ))}
                  </tr>
                  {group.modules.map(mod => (
                    <tr key={mod} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]">
                      <td className="sticky left-0 z-10 px-3 py-1.5 pl-6 text-[#555]" style={{ backgroundColor: 'var(--card-bg)' }}>
                        {MODULE_LABELS[mod] || mod}
                      </td>
                      {roles.map(role => (
                        <td key={role} className="px-2 py-1.5 text-center">
                          {role === 'Admin' ? (
                            <input type="checkbox" checked={true} disabled className="w-3.5 h-3.5 accent-[#002FA7] opacity-50" />
                          ) : (
                            <input type="checkbox"
                              checked={(permissions[role] || []).includes(mod)}
                              onChange={() => toggle(role, mod)}
                              className="w-3.5 h-3.5 accent-[#002FA7] cursor-pointer"
                              data-testid={`perm-${role}-${mod}`} />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
